import React from 'react';
import { Button, Tag } from '@scalingworks/react-admin-ui';
import { useTranslate } from '@refinedev/core';
import { ModalWrapper } from '~/components';
import { formatCurrency } from '~/resources/helpers';
import dayjs from 'dayjs';
import { FiTrendingUp, FiTrendingDown, FiDollarSign, FiRefreshCw } from 'react-icons/fi';
import { HiOutlineSparkles } from 'react-icons/hi';
import { IoWalletOutline } from 'react-icons/io5';

interface TransactionModalProps {
  visible: boolean;
  onOpenChange: (visible: boolean) => void;
  transactions: any[];
  currency: string;
}

const TransactionTypeConfig = {
  SPENDING: { 
    icon: FiTrendingDown,
    label: 'Spending'
  },
  RELOAD: { 
    icon: FiTrendingUp,
    label: 'Reload'
  },
  ADJUSTMENT: { 
    icon: FiRefreshCw,
    label: 'Adjustment'
  },
  BONUS_CREDIT: { 
    icon: HiOutlineSparkles,
    label: 'Bonus'
  },
} as const;

export const TransactionModal: React.FC<TransactionModalProps> = ({
  visible,
  onOpenChange,
  transactions,
  currency,
}) => {
  const t = useTranslate();

  return (
    <ModalWrapper
      open={visible}
      setOpen={onOpenChange}
      title="Transaction History"
      hideButton={true}
      contentClassName="max-w-3xl"
    >
      <section className="p-4">
        <div className="max-h-96 overflow-y-auto">
          {transactions.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <IoWalletOutline className="text-gray-400" size={32} />
              </div>
              <p className="text-gray-500">No transactions yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {transactions.map((transaction) => {
                const config = TransactionTypeConfig[transaction.type as keyof typeof TransactionTypeConfig];
                const Icon = config?.icon || FiDollarSign;
                
                return (
                  <div key={transaction.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <Icon className="text-gray-600" size={14} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-900">
                              {config?.label || transaction.type}
                            </span>
                            <span className="text-xs text-gray-500">
                              {dayjs(transaction.createdAt).format('MMM DD, YYYY HH:mm')}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 break-words">{transaction.description || 'No description'}</p>
                        </div>
                      </div>
                      <div className={`text-lg font-bold ${transaction.amount >= 0 ? 'text-green-600' : 'text-red-600'} flex-shrink-0`}>
                        {transaction.amount >= 0 ? '+' : ''}{formatCurrency(transaction.amount, currency)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </ModalWrapper>
  );
};